<template>
  <div class="property-community-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('propertyCommunity.page.title')" :subtitle="$t('propertyCommunity.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          {{ $t('propertyCommunity.list.add') }}
        </el-button>
        <el-button icon="el-icon-back" @click="goBack">
          {{ $t('common.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.communityId" clearable class="w-full"
            :placeholder="$t('propertyCommunity.search.communityId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.name" clearable class="w-full"
            :placeholder="$t('propertyCommunity.search.name')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="refreshList">
        <template #left>
          <span class="ds-panel__title">{{ $t('propertyCommunity.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('propertyCommunity.page.empty')" @retry="refreshList">
        <el-table-column prop="communityId" :label="$t('propertyCommunity.table.communityId')" align="center"
          width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.communityId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('propertyCommunity.table.name')" align="left" min-width="160"
          show-overflow-tooltip />
        <el-table-column prop="nearbyLandmarks" :label="$t('propertyCommunity.table.nearbyLandmarks')" align="left"
          min-width="180" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.nearbyLandmarks || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="cityName" :label="$t('propertyCommunity.table.cityCode')" align="center" width="200"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.cityName || '-' }}
          </template>
        </el-table-column>
        <!-- 状态 Tag：颜色仅作辅助，始终带文字（WCAG 1.4.1） -->
        <el-table-column prop="stateName" :label="$t('propertyCommunity.table.state')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('propertyCommunity.list.quit') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('propertyCommunity.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 组件 -->
    <add-property-community ref="addDialog" @success="handleSuccess" />
    <edit-property-community ref="editDialog" @success="handleSuccess" />
    <delete-property-community ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import {
  listCommunitys,
} from '@/api/community/propertyCommunityApi'
import AddPropertyCommunity from '@/components/community/AddPropertyCommunity'
import EditPropertyCommunity from '@/components/community/EditPropertyCommunity'
import DeletePropertyCommunity from '@/components/community/DeletePropertyCommunity'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'PropertyCommunityList',
  components: {
    AddPropertyCommunity,
    EditPropertyCommunity,
    DeletePropertyCommunity,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        communityId: '',
        name: '',
        memberId: this.$route.query.storeId || ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, total } = await listCommunitys(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取小区数据失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('propertyCommunity.page.loadFailed')
        this.$message.error(this.$t('propertyCommunity.fetchError'))
      } finally {
        this.loading = false
      }
    },
    /**
     * 刷新当前页（工具条刷新 / 错误态重试）
     */
    refreshList() {
      this.getList()
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        communityId: '',
        name: '',
        memberId: this.$route.query.storeId || ''
      }
      this.handleSearch()
    },
    handleAdd() {
      this.$refs.addDialog.open({
        storeId: this.searchForm.memberId
      })
    },
    handleEdit(row) {
      this.$refs.editDialog.open({
        ...row,
        storeId: this.searchForm.memberId
      })
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open({
        ...row,
        memberId: this.searchForm.memberId
      })
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    goBack() {
      this.$router.go(-1)
    },
    /**
     * 小区状态语义类型（s_community.state）：
     * 1000 待审核 / 1100 审核通过 / 1200 拒绝审核
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'community',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.property-community-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
